import _ from 'lodash';
import { pipe } from 'ramda';

import {
  ApiWeeklyScheduleEvent,
  DraftVenue,
  ApiWeeklySchedule,
  UpdateDraftVenueRequest,
  OpeningTimes,
} from 'apis/merchant-onboarding-service/types';
import {
  fromWeeklyScheduleInputEntriesToWeeklySchedule,
  fromWeeklyScheduleToTimePairsByWeekday,
  fromWeeklyScheduleToWeeklyScheduleInputEntries,
} from 'common/components/weekly-schedules';
import {
  TimePair,
  TimePairsByWeekday,
} from 'common/components/weekly-schedules/fromWeeklyScheduleToTimePairsByWeekday';
import { WeeklySchedule, WeeklyScheduleEvent } from 'common/components/weekly-schedules/types';

import { NormalOpeningHoursEntries, NormalOpeningHoursEntry } from './OpeningHoursTab/types';
import {
  DEFAULT_CLOSE_DELIVERIES,
  DEFAULT_COMPLICATED_OPENING_HOURS,
  DEFAULT_NORMAL_OPENING_HOURS,
  DEFAULT_OPENING_HOURS_TYPE,
  TWENTY_FOUR_SEVEN_WEEKLY_SCHEDULE,
} from './initialValues';
import { DraftVenueFormValues } from './types';
import { ValidDraftVenueFormValues } from './validation/validationSchema';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const HOUR_IN_MILLISECONDS = 3600000;
const MINUTE_IN_MILLISECONDS = 60000;
const DAY_IN_MILLISECONDS = 24 * HOUR_IN_MILLISECONDS;
const WEEK_IN_MILLISECONDS = 7 * DAY_IN_MILLISECONDS;

const unwrapMongoDates = (weeklySchedule: WeeklySchedule): ApiWeeklySchedule => {
  const unwrapMongoDate = (event: WeeklyScheduleEvent) => ({ type: event.type, value: event.value.$date });
  return {
    monday: weeklySchedule.monday.map(unwrapMongoDate),
    tuesday: weeklySchedule.tuesday.map(unwrapMongoDate),
    wednesday: weeklySchedule.wednesday.map(unwrapMongoDate),
    thursday: weeklySchedule.thursday.map(unwrapMongoDate),
    friday: weeklySchedule.friday.map(unwrapMongoDate),
    saturday: weeklySchedule.saturday.map(unwrapMongoDate),
    sunday: weeklySchedule.sunday.map(unwrapMongoDate),
  };
};

const wrapMongoDates = (weeklySchedule: ApiWeeklySchedule): WeeklySchedule => {
  const wrapMongoDate = (event: ApiWeeklyScheduleEvent) => ({ type: event.type, value: { $date: event.value } });
  return {
    monday: weeklySchedule.monday.map(wrapMongoDate),
    tuesday: weeklySchedule.tuesday.map(wrapMongoDate),
    wednesday: weeklySchedule.wednesday.map(wrapMongoDate),
    thursday: weeklySchedule.thursday.map(wrapMongoDate),
    friday: weeklySchedule.friday.map(wrapMongoDate),
    saturday: weeklySchedule.saturday.map(wrapMongoDate),
    sunday: weeklySchedule.sunday.map(wrapMongoDate),
  };
};

const transformOpeningTimes = (formValues: ValidDraftVenueFormValues): OpeningTimes => {
  const { openingHoursType, normalOpeningHours } = formValues;

  if (openingHoursType === 'twenty-four-seven') {
    return {
      type: 'twenty-four-seven',
      weeklySchedule: TWENTY_FOUR_SEVEN_WEEKLY_SCHEDULE,
    };
  }

  if (openingHoursType === 'normal') {
    const isTwentyFourHours = Object.values(normalOpeningHours).every(value =>
      _.isEqual(value, { isOpen: true, from: 0, to: 0 }),
    );

    if (isTwentyFourHours) {
      return {
        type: 'normal',
        weeklySchedule: TWENTY_FOUR_SEVEN_WEEKLY_SCHEDULE,
      };
    }

    return {
      type: 'normal',
      weeklySchedule: Object.entries(normalOpeningHours).reduce(
        (acc, [currentDay, value]: [keyof NormalOpeningHoursEntries, NormalOpeningHoursEntry]) => {
          // "from" and "to" values are in minutes
          const { isOpen, from, to } = value;

          if (isOpen) {
            const openValue = from * MINUTE_IN_MILLISECONDS;
            const closeValue = to === 0 ? 24 * HOUR_IN_MILLISECONDS : to * MINUTE_IN_MILLISECONDS;
            const indexOfCurrentDay = DAYS_OF_WEEK.indexOf(currentDay);

            const indexOfPrevDay = (indexOfCurrentDay + 7 - 1) % 7;
            const prevDayOfWeek = DAYS_OF_WEEK[indexOfPrevDay];

            const prevDayClosingTime = normalOpeningHours[prevDayOfWeek].to * MINUTE_IN_MILLISECONDS;
            const prevDayIsOpen = normalOpeningHours[prevDayOfWeek].isOpen; // For if previous day is closed ( to value = 0 ) and current day open is 0
            const openIsTouchingPrevClose = prevDayIsOpen && prevDayClosingTime === openValue; // eg. 21:00 - 02:00, 02:00 - 12:00 || 21:00-24:00, 00:00-12:00

            if (!openIsTouchingPrevClose) {
              acc[currentDay].push({ type: 'open', value: openValue });
            }

            const closingTimeIsOnTheNextDay = closeValue < openValue;

            const indexOfNextDay = (indexOfCurrentDay + 1) % 7;
            const nextDayOfWeek = DAYS_OF_WEEK[indexOfNextDay];
            const nextDayIsOpen = normalOpeningHours[nextDayOfWeek].isOpen;

            const closeIsTouchingNextOpen =
              normalOpeningHours[nextDayOfWeek].from * MINUTE_IN_MILLISECONDS === closeValue;

            const closeIsTouchingNextOpenAtMidnight =
              nextDayIsOpen && normalOpeningHours[nextDayOfWeek].from === 0 && closeValue === 24 * HOUR_IN_MILLISECONDS;

            if (closingTimeIsOnTheNextDay && !closeIsTouchingNextOpen) {
              acc[nextDayOfWeek].push({ type: 'close', value: closeValue });
            } else if (!closingTimeIsOnTheNextDay && !closeIsTouchingNextOpenAtMidnight) {
              acc[currentDay].push({ type: 'close', value: closeValue });
            }
          }

          return acc;
        },
        {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: [],
        } as ApiWeeklySchedule,
      ),
    };
  }

  return {
    type: 'complicated',
    weeklySchedule: unwrapMongoDates(
      fromWeeklyScheduleInputEntriesToWeeklySchedule(formValues.complicatedOpeningHours),
    ),
  };
};

export const transformNormalOpeningHoursFromApi = (weeklySchedule: ApiWeeklySchedule): NormalOpeningHoursEntries => {
  const timePairs = fromWeeklyScheduleToTimePairsByWeekday(wrapMongoDates(weeklySchedule));

  const hourInMinutes = 60;

  return Object.entries(timePairs).reduce(
    (acc, [day, values]: [keyof TimePairsByWeekday, TimePair[]]) => {
      const dayIsClosed = values.length === 0;

      if (dayIsClosed) {
        return acc;
      } else {
        const [value] = values;
        const { start, end } = value;

        acc[day] = { isOpen: true, from: start, to: end === 24 * hourInMinutes ? 0 : end };
      }

      return acc;
    },
    {
      monday: { isOpen: false, from: 0, to: 0 },
      tuesday: { isOpen: false, from: 0, to: 0 },
      wednesday: { isOpen: false, from: 0, to: 0 },
      thursday: { isOpen: false, from: 0, to: 0 },
      friday: { isOpen: false, from: 0, to: 0 },
      saturday: { isOpen: false, from: 0, to: 0 },
      sunday: { isOpen: false, from: 0, to: 0 },
    } as NormalOpeningHoursEntries,
  );
};

export const offsetClosingTimes = (offset: number, weeklySchedule: ApiWeeklySchedule): ApiWeeklySchedule => {
  type TimeSpan = {
    /** milliseconds since the start of the week (Monday 00:00) */
    start: number;
    /** milliseconds */
    duration: number;
  };

  type Event = {
    type: 'open' | 'close';
    /** milliseconds since the start of the week (Monday 00:00) */
    time: number;
  };

  // Don't handle positive offsets, because there are no use cases for that at this time. Positive
  // offsets would introduce additional complexity, because they can produce overlapping timespans.
  if (offset > 0) {
    throw new Error('positive offsets are not supported');
  }

  const weeklyScheduleToEvents = (weeklySchedule: ApiWeeklySchedule): Event[] =>
    DAYS_OF_WEEK.flatMap((currentDayOfWeek, dayOfWeekIndex) =>
      weeklySchedule[currentDayOfWeek].map(event => ({
        type: event.type,
        time: (dayOfWeekIndex * DAY_IN_MILLISECONDS + event.value) % WEEK_IN_MILLISECONDS, // time since Monday 00:00
      })),
    );

  const eventsToTimespans = (events: Event[]): TimeSpan[] => {
    const openEvents = events.filter(event => event.type === 'open');
    const closeEvents = events.filter(event => event.type === 'close');
    return openEvents.map(openEvent => {
      const closeEvent = closeEvents.find(closeEvent => closeEvent.time > openEvent.time) || closeEvents[0];
      return {
        start: openEvent.time,
        duration:
          (closeEvent.time > openEvent.time ? closeEvent.time : closeEvent.time + WEEK_IN_MILLISECONDS) -
          openEvent.time,
      };
    });
  };

  const offsetTimespanEndings = (offset: number, timeSpans: TimeSpan[]): TimeSpan[] => {
    const isTwentyFourSeven = timeSpans.length === 1 && timeSpans[0].duration === WEEK_IN_MILLISECONDS;
    return isTwentyFourSeven
      ? timeSpans
      : timeSpans
          .map(timeSpan => {
            return {
              start: timeSpan.start,
              duration: timeSpan.duration + offset,
            };
          })
          .filter(timeSpan => timeSpan.duration > 0);
  };

  const timeSpansToEvents = (timeSpans: TimeSpan[]): Event[] =>
    timeSpans.reduce((acc, timeSpan) => {
      return [
        ...acc,
        { type: 'open', time: timeSpan.start },
        { type: 'close', time: (timeSpan.start + timeSpan.duration) % WEEK_IN_MILLISECONDS },
      ];
    }, []);

  const eventsToWeeklySchedule = (events: Event[]): ApiWeeklySchedule =>
    events.reduce(
      (acc, event) => {
        let value = event.time % DAY_IN_MILLISECONDS;
        let dayIndex = Math.floor(event.time / DAY_IN_MILLISECONDS);
        if (event.type === 'close' && value === 0) {
          dayIndex = (dayIndex + 7 - 1) % 7;
          value = DAY_IN_MILLISECONDS;
        }
        const dayOfWeek = DAYS_OF_WEEK[dayIndex];
        return {
          ...acc,
          [dayOfWeek]: [...acc[dayOfWeek], { type: event.type, value }],
        };
      },
      {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      } as ApiWeeklySchedule,
    );

  return pipe(
    weeklyScheduleToEvents,
    eventsToTimespans,
    timeSpans => offsetTimespanEndings(offset, timeSpans),
    timeSpansToEvents,
    eventsToWeeklySchedule,
  )(weeklySchedule);
};

export const draftVenueFormValuesToApi = (
  formValues: ValidDraftVenueFormValues,
): Omit<UpdateDraftVenueRequest, 'status'> => {
  const openingTimes = transformOpeningTimes(formValues);
  return {
    name: formValues.name,
    address: formValues.address,
    website: formValues.website || null,
    postCode: formValues.postCode,
    city: formValues.city,
    country: formValues.country,
    openingTimes,
    deliveryTimes: {
      type: formValues.closeDeliveries,
      weeklySchedule: offsetClosingTimes(
        {
          'at-closing': 0,
          '30-minutes-before-closing': -30 * 60 * 1000,
          '60-minutes-before-closing': -60 * 60 * 1000,
        }[formValues.closeDeliveries],
        openingTimes.weeklySchedule,
      ),
    },
  };
};

export const fromApiToDraftVenueFormValues = (draftVenue: DraftVenue): DraftVenueFormValues => {
  const formValues = {
    name: draftVenue.name,
    address: draftVenue.address,
    website: draftVenue.website || undefined,
    postCode: draftVenue.postCode,
    city: draftVenue.city,
    country: draftVenue.country,
    status: draftVenue.status,
    openingHoursType: draftVenue.openingTimes?.type || DEFAULT_OPENING_HOURS_TYPE,
    normalOpeningHours:
      draftVenue.openingTimes?.type === 'normal'
        ? transformNormalOpeningHoursFromApi(draftVenue.openingTimes.weeklySchedule)
        : DEFAULT_NORMAL_OPENING_HOURS,
    complicatedOpeningHours:
      draftVenue.openingTimes?.type === 'complicated'
        ? fromWeeklyScheduleToWeeklyScheduleInputEntries(wrapMongoDates(draftVenue.openingTimes.weeklySchedule))
        : DEFAULT_COMPLICATED_OPENING_HOURS,
    closeDeliveries: draftVenue.deliveryTimes?.type || DEFAULT_CLOSE_DELIVERIES,
  };

  return formValues;
};
